import h from 'hyperscript';
import moreIcon from '@disy/cadenza-icons/more.svg';
import viewsIcon from '@disy/cadenza-icons/views.svg';
import { unByKey } from 'ol/Observable';

import 'ui/switch/switch';
import { createButton, createIconButton } from 'ui/button/button';
import { setupMenuButton } from 'ui/menu/menu';

import { getActionContext } from 'cadenza/action-context';
import { Filter as FilterAction } from 'cadenza/map/layer-actions/filter';
import { getLayerProcessings } from 'cadenza/api-client/processing-api';
import { ActionsName } from 'cadenza/map/layer-actions/layer-actions-name';
import { ExportAction } from 'cadenza/map/layer-actions/export-action';
import { LayerProperty } from 'cadenza/map/layer-property';
import { getLegendLayer } from 'cadenza/map/legend/layer/legend-layer';
import { MapProperty } from 'cadenza/map/map-property';
import { createProcessesButton } from 'cadenza/processes/processes-button-util';
import { emptyIcon, icon } from 'cadenza/utils/icon/icon';
import { logger } from 'cadenza/utils/logging';
import { debounce } from 'cadenza/utils/debounce';
import { getLayerTasks } from 'cadenza/map/layer-tasks';
import { createShowLayerConfigurationPanelAction, getTemplateActions } from 'cadenza/map/layer-actions/show-layer-configuration-menu';
import layerActionsI18n from 'cadenza/map/layer-actions/layer-actions.properties';
import processesI18n from 'cadenza/processes/messages.properties';

import './layer-action-toolbar.css';
import i18n from './layer-action-toolbar.properties';

const COMPONENT_NAME = 'd-layer-action-toolbar';

export class LayerActionToolbar extends HTMLElement {

  #layerTasks;
  #inCustomAppContext;
  _menuButton;

  /**
   *
   * @param {object} layer - The layer to request actions for
   * @param {object} map - The map to request actions for
   * @param {object} [options={}] - optional arguments
   * @param {boolean} [options.isInHeaderToolbar=false] - flag that tells whether toolbar is shown in the header
   * @param {boolean} [options.inCustomAppContext=false] - flag that tells whether toolbar is shown in custom application context
   *
   */
  constructor (layer, map, {
    isInHeaderToolbar = false,
    inCustomAppContext = false
  } = {}) {
    super();
    this._layer = layer;
    this._map = map;
    this.#layerTasks = getLayerTasks(layer);
    this._isInHeaderToolbar = isInHeaderToolbar;
    this.#inCustomAppContext = inCustomAppContext;

    this.classList.add(COMPONENT_NAME);

    const allActionsMapping = map.get(MapProperty.LAYER_ACTIONS_PROVIDER)(layer);
    layer.set(LayerProperty.ALL_ACTIONS_MAPPING, allActionsMapping);
    this._debouncedUpdate = debounce(() => this._update(), 200);
  }

  cloneNode () {
    const clonedLayerActionToolbar = new LayerActionToolbar(this._layer, this._map);
    clonedLayerActionToolbar.connectedCallback();
    const clonedChildren = [ ...this.children ].map(el => el.cloneNode(true));
    clonedLayerActionToolbar.append(...clonedChildren);
    return clonedLayerActionToolbar;
  }

  _initActionButtons (shortcutActionTypes, allActionsMapping, layerID) {
    this._shortcutActionButtons = [];
    this._dropDownActionButtonsNoSelectionNeeded = [];
    this._dropDownActionButtonsSelectionNeeded = [];
    shortcutActionTypes.forEach(actionType => {
      const action = allActionsMapping[actionType];
      if (action) {
        const button = this._actionButton(layerID, action, actionType, true);
        this._shortcutActionButtons.push(button);
      }
    });
    Object.keys(allActionsMapping).forEach(actionType => {
      const isShortcutAction = shortcutActionTypes.includes(actionType);
      if (!isShortcutAction) {
        const action = allActionsMapping[actionType];
        const needsSelection = action.requiresObjectSelection();
        const button = this._actionButton(layerID, action, actionType, false);
        if (needsSelection) {
          this._dropDownActionButtonsSelectionNeeded.push(button);
        } else {
          this._dropDownActionButtonsNoSelectionNeeded.push(button);
        }
      }
    });
  }

  connectedCallback () {
    this._debouncedUpdate();
    this._eventKeys = [
      this._layer.on('propertychange', () => this._debouncedUpdate()),
      this._layer.getSource().on('change', () => this._debouncedUpdate()),
      this._map.on('moveend', () => this._debouncedUpdate())
    ];
  }

  disconnectedCallback () {
    unByKey(this._eventKeys);
  }

  _update () {
    const layer = this._layer;
    const map = this._map;
    const allActionsMapping = layer.get(LayerProperty.ALL_ACTIONS_MAPPING);
    const shortcutActions = layer.get(LayerProperty.SHORTCUT_ACTIONS);
    this._initActionButtons(shortcutActions, allActionsMapping, layer.get(LayerProperty.ID));
    Promise.all([
      getLayerTemplateActions(layer, map),
      this._updateActionButtons(this._shortcutActionButtons, true),
      this._updateActionButtons([ ...this._dropDownActionButtonsNoSelectionNeeded, ...this._dropDownActionButtonsSelectionNeeded ])
    ]).then(([ templateActions ]) => {
      // For some reason " this.innerHTML='' " breaks the rendering of filter icon svg element
      while (this.firstChild) {
        this.removeChild(this.firstChild);
      }

      this._menuButton = this._createMenuButton(
        layer,
        this._dropDownActionButtonsNoSelectionNeeded,
        this._dropDownActionButtonsSelectionNeeded,
        templateActions);

      this._createAndAddViewsButtonIfAvailable(shortcutActions, this._menuButton, layer);

      this.append(...this._shortcutActionButtons, this._menuButton);
    }).catch(error => {
      logger.error('Error by updating layer toolbar action buttons', error);
    });
  }

  _createAndAddViewsButtonIfAvailable (shortCutActions, menuButton, layer) {
    if (!this.#inCustomAppContext
      && !this._map.get(MapProperty.EDITING_LAYER)
      && layer.get(LayerProperty.IS_FILTERABLE)
      && !layer.get(LayerProperty.IS_TRANSIENT_REPOSITORY_ITEM_THEME)) {
      const processingShortcutActionIndex = shortCutActions.findIndex(actionType => actionType === 'Processing');
      const processingIsShortcutAction = processingShortcutActionIndex > -1;
      const layerID = layer.get(LayerProperty.ID);
      const variant = (this._isInHeaderToolbar || !processingIsShortcutAction) ? 'borderless' : null;
      this._processesButton = createProcessesButton(() => getLayerProcessings(layerID), {
        variant,
        menuButton: processingIsShortcutAction ? null : menuButton
      });
      if (processingIsShortcutAction) {
        // Add 'Views' as shortcut at the position where it was configured to be.
        this._shortcutActionButtons.splice(processingShortcutActionIndex, 0, this._processesButton);
      } else {
        // Add 'Views' as first dropDown action if it was not configured to be a shortcut.
        this._dropDownActionButtonsNoSelectionNeeded.splice(0, 0, this._processesButton);
      }
    }
  }

  _updateActionButtons (actionButtons, isShortcutAction) {
    const context = getActionContext(this._map);
    return Promise.all(
      actionButtons.map(actionButton => {
        const action = actionButton.action;
        actionButton.disabled = action.disabled;
        return action.isSupported(context).then(isSupported => {
          actionButton.hidden = !isSupported;
          if (isShortcutAction && action.hasIcon()) {
            actionButton.title = action.label;
          } else if (actionButton.lastChild) {
            actionButton.lastChild.textContent = action.label;
          }
        }).catch(error => {
          logger.error(`Cannot determine if action "${actionButton.actionType}" is supported`, error);
          actionButton.hidden = true;
        });
      }));
  }

  _actionButton (layerId, action, actionType, isShortcutAction) {
    const executeAction = (event, button) => {
      // prevent duplicate execution until the action finishes, fails executing, or is cancelled
      // TODO_LATER (babenhauserheide, 2021-09-21, CADENZA-23558): use a proper mutex?
      if (isShortcutAction) {
        event.stopPropagation();
      }
      if (button._isActionExecuting) {
        return;
      }
      button._isActionExecuting = true;
      if (action.isCancellable()) {
        this._onCancellableActionStart(layerId, action, actionType);
      }
      action.execute(button, event)
        .then(() => {
          if (action.isCancellable() && !action.isCancelled()) {
            this._onCancellableActionEnd(layerId, actionType);
          }
        })
        .catch(error => {
          logger.error(`Failed to execute action ${actionType}`, error);
          this._onCancellableActionEnd(layerId, actionType);
        })
        .finally(() => {
          button._isActionExecuting = false;
        });
    };
    if (action.isCancellable()) {
      action.on('cancelled', () => {
        button._isActionExecuting = false;
        this._onCancellableActionEnd(layerId, actionType);
      }, 1);
    }

    const classes = getActionButtonClasses(isShortcutAction, action, this._isInHeaderToolbar);

    const button = h(`button.button${classes.join('')}`, {
      type: 'button',
      name: `legend-layer-action-${layerId}-${actionType}`,
      title: isShortcutAction && action.hasIcon() ? action.label : '',
      onclick: (event) => executeAction(event, button)
    });
    button.action = action;
    button.actionType = actionType;
    if (!this._isInHeaderToolbar
      && this._layer.get(LayerProperty.HAS_EMPTY_REQUIRED_CONDITIONS)
      && action instanceof FilterAction) {
      button.classList.add('is-warning');
    }

    action.on('change:active', () => button.classList.toggle('active', action.active));

    /**
     * If action is expandable (e.g. ChooseLayerTemplate) and it's a shortcut action it should be expanded onclick
     * If action is expandable but it is NOT a shortcut action it should be expanded on mouse hover.
     * By default all actions are NOT expandable (see Action.isExpandable())
     */
    if (action.isExpandable() === true && !isShortcutAction) {
      button.addEventListener('mouseenter', (event) => executeAction(event, button));
    }

    if (this._isInHeaderToolbar) {
      button.append(action.icon || emptyIcon(), h('span.button--label.ellipsis', action.label));
    } else {
      if (isShortcutAction) {
        // edge case: export action has an icon in the header toolbar, but not in the legend
        //  if we need this for more buttons, we should add a new property to LayerAction instead
        //  of adding instanceof checks.
        if (action instanceof ExportAction) {
          button.append(h('span.button--label.ellipsis', action.label));
        } else {
          button.append(action.icon || h('span.button--label.ellipsis', action.label));
        }
      } else {
        button.append(action.icon || emptyIcon(), h('span.button--label.ellipsis', action.label));
      }
    }

    return button;
  }

  _createMenuButton (layer, containedButtonsNoSelectionNeeded, containedButtonsSelectionNeeded, templateActions) {
    const isAnyButtonVisible = [ ...containedButtonsNoSelectionNeeded, ...containedButtonsSelectionNeeded ].some(button => !button.hidden);

    const moreActionsButton = this._isInHeaderToolbar
      ? createButton([ icon(moreIcon), h('span.button--label.ellipsis', i18n('layer.dropdown.label')) ], {
        variant: 'borderless',
        hidden: !isAnyButtonVisible
      })
      : createIconButton(icon(moreIcon), i18n('layer.dropdown.title'), {
        id: `legend-layer-actions-dropdown-${layer.get(LayerProperty.ID)}`,
        hidden: !isAnyButtonVisible,
        onclick: event => event.stopPropagation()
      });

    const contentProvider = () => {
      const visibleButtonsNoSelectionNeeded = containedButtonsNoSelectionNeeded.filter(button => !button.hidden);
      const visibleButtonsSelectionNeeded = containedButtonsSelectionNeeded.filter(button => !button.hidden);

      const content = [ ...visibleButtonsNoSelectionNeeded.map(btn => this._getMenuItemDefinitionFromButton(btn)) ];

      const showLayerConfigurationIndex = visibleButtonsNoSelectionNeeded.findIndex(button => button.actionType === ActionsName.ShowLayerConfigurationMenu);
      if (showLayerConfigurationIndex !== -1) {
        const showLayerConfigurationAction = visibleButtonsNoSelectionNeeded[showLayerConfigurationIndex].action;
        content[showLayerConfigurationIndex] = createShowLayerConfigurationSubmenuItem(showLayerConfigurationAction.icon, showLayerConfigurationAction.label, {
          layer: this._layer,
          map: this._map,
          templateActions
        });
      }

      const numberOfSelectedFeatures = layer.get(LayerProperty.SELECTED_FEATURE_IDS).length;
      if (numberOfSelectedFeatures > 0) {
        content.push({
          label: i18n('layer.itemSelected', { count: numberOfSelectedFeatures }),
          group: visibleButtonsSelectionNeeded.map(btn => this._getMenuItemDefinitionFromButton(btn))
        });
      }
      return content;
    };

    setupMenuButton(moreActionsButton, contentProvider);

    return moreActionsButton;
  }

  _onCancellableActionStart (layerId, action, actionType) {
    const taskId = this._addActionTask(layerId, action);
    this._setTaskIdInProgress(actionType, taskId);
    Array.from(document.getElementsByName(`legend-layer-action-${layerId}-${actionType}`)).forEach(
      (actionButton) => {
        actionButton.disabled = true;
      });
  }

  _addActionTask (layerId, action) {
    const legendLayer = getLegendLayer(layerId);
    if (legendLayer) {
      return this.#layerTasks.registerTask(() => action.cancel());
    } else {
      return null;
    }
  }

  _onCancellableActionEnd (layerId, actionType) {
    Array.from(document.getElementsByName(`legend-layer-action-${layerId}-${actionType}`)).forEach(
      (actionButton) => {
        actionButton.disabled = false;
      });
    this._removeActionStatus(layerId, actionType);
    this._deleteTaskIdInProgress(actionType);
  }

  _removeActionStatus (layerId, actionType) {
    const legendLayer = getLegendLayer(layerId);
    if (legendLayer) {
      const taskIdInProgress = this._getTaskIdInProgress(actionType);
      if (taskIdInProgress) {
        this.#layerTasks.completeTask(taskIdInProgress);
      }
    }
  }

  _setTaskIdInProgress (actionType, taskId) {
    let taskIdInProgressByActionType = this._layer.get(LayerProperty.TASK_ID_IN_PROGRESS_BY_ACTION_TYPE);
    if (!taskIdInProgressByActionType) {
      taskIdInProgressByActionType = new Map();
      this._layer.set(LayerProperty.TASK_ID_IN_PROGRESS_BY_ACTION_TYPE, taskIdInProgressByActionType);
    }
    taskIdInProgressByActionType.set(actionType, taskId);
  }

  _deleteTaskIdInProgress (actionType) {
    const taskIdInProgressByActionType = this._layer.get(LayerProperty.TASK_ID_IN_PROGRESS_BY_ACTION_TYPE);
    if (taskIdInProgressByActionType) {
      taskIdInProgressByActionType.delete(actionType);
    }
  }

  _getTaskIdInProgress (actionType) {
    const taskIdInProgressByActionType = this._layer.get(LayerProperty.TASK_ID_IN_PROGRESS_BY_ACTION_TYPE);
    if (taskIdInProgressByActionType) {
      return taskIdInProgressByActionType.get(actionType);
    } else {
      return null;
    }
  }

  _getMenuItemDefinitionFromButton (button) {
    if (button === this._processesButton) {
      return {
        label: processesI18n('processesButton'),
        icon: icon(viewsIcon),
        execute: () => button.click(),
        id: button.id
      };
    } else {
      const btnAction = button.action;
      return {
        label: btnAction.label,
        icon: btnAction.icon,
        execute: btnAction.execute,
        disabled: !!this._getTaskIdInProgress(button.actionType),
        id: button.id
      };
    }
  }

}

customElements.define(COMPONENT_NAME, LayerActionToolbar);

function getLayerTemplateActions (layer, map) {
  const shortcutActions = layer.get(LayerProperty.SHORTCUT_ACTIONS);
  const hasApplicableTemplates = layer.get(LayerProperty.HAS_APPLICABLE_TEMPLATES);
  // ShowLayerConfigurationMenu retrieves the layerTemplates itself when used as a shortcutAction
  const isShowLayerConfigurationMenuActionAShortcutAction = shortcutActions.includes(ActionsName.ShowLayerConfigurationMenu);
  if (hasApplicableTemplates && !isShowLayerConfigurationMenuActionAShortcutAction) {
    return getTemplateActions(layer, map)
      .catch(error => {
        logger.error('Could not retrieve templates', error);
        return []; // Do not reject the promise returned from here, otherwise we will fail to create any layer action button, even unrelated ones
      });
  }
  return Promise.resolve();
}

function getActionButtonClasses (isShortcutAction, action, isInHeaderToolbar) {
  if (isInHeaderToolbar || !isShortcutAction) {
    return [ '.button-borderless' ];
  } else if (action.hasIcon(isShortcutAction)) {
    return [ '.button-icon' ];
  }
  return [];
}

function createShowLayerConfigurationSubmenuItem (subMenuIcon, label, { layer, map, templateActions }) {
  return {
    icon: subMenuIcon,
    label,
    subMenu: () => {
      return [
        ...(layer.get(LayerProperty.CONFIGURATION_CONSTRAINTS).configurable ? [ createShowLayerConfigurationPanelAction(layer, map) ] : []),
        ...(templateActions && templateActions.length ? [ { label: layerActionsI18n('templates'), group: templateActions } ] : [])
      ];
    }
  };
}
